import cn from 'classnames';
// Ui3
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Types
import { FightDraft } from './fightCardTypes';
// Styles
import styles from './MatchupsList.module.scss';

export type MatchupsListProps = {
  drafts: FightDraft[];
  onEdit?: () => void;
  canControl?: boolean;
};

const isBoutConfigured = (fight: FightDraft): boolean =>
  !!fight.rounds && !!fight.roundMinutes;

const Avatar = ({
  name,
  avatarURL,
}: {
  name: string;
  avatarURL: string | null;
}) =>
  avatarURL ? (
    <img className={styles.avatarImage} src={avatarURL} alt="" />
  ) : (
    <span className={styles.avatar}>
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );

const MatchupsList = ({
  drafts,
  onEdit,
  canControl = true,
}: MatchupsListProps) => {
  const hasUnconfigured = drafts.some((fight) => !isBoutConfigured(fight));

  return (
    <div className={styles.root}>
      {hasUnconfigured && (
        <div className={styles.banner}>
          <MillionsIcon name="timer" className={styles.bannerIcon} />
          <Text variant="body3Regular" color="white">
            Add timer and number of rounds for each fight.
          </Text>
        </div>
      )}

      <ol className={styles.list}>
        {drafts.map((fight, index) => {
          const configured = isBoutConfigured(fight);
          return (
            <li key={index} className={styles.bout}>
              <div className={styles.boutHeader}>
                <span className={styles.boutTitle}>Bout {index + 1}</span>
                {fight.isMainEvent && (
                  <span className={styles.mainEventBadge}>Main Event</span>
                )}
                {configured ? (
                  <span className={styles.rounds}>{fight.rounds} Rounds</span>
                ) : (
                  <span className={cn(styles.rounds, styles.roundsWarning)}>
                    <MillionsIcon name="clock" className={styles.roundsIcon} />
                    Add Rounds
                  </span>
                )}
              </div>

              <div className={styles.fighters}>
                <span className={styles.fighter}>
                  <Avatar
                    name={fight.fighterA.name}
                    avatarURL={fight.fighterA.avatarURL}
                  />
                  <span className={styles.fighterName}>
                    {fight.fighterA.name || 'TBD'}
                  </span>
                </span>
                <span className={styles.vs}>VS</span>
                <span className={cn(styles.fighter, styles.fighterRight)}>
                  <span className={styles.fighterName}>
                    {fight.fighterB.name || 'TBD'}
                  </span>
                  <Avatar
                    name={fight.fighterB.name}
                    avatarURL={fight.fighterB.avatarURL}
                  />
                </span>
              </div>
            </li>
          );
        })}
      </ol>

      {canControl && onEdit && (
        <Button
          variant="secondary"
          size="small"
          className={styles.editButton}
          onClick={onEdit}
        >
          <MillionsIcon name="pencil" className={styles.editIcon} />
          {drafts.length ? 'Edit Matchups' : 'Add Matchup'}
        </Button>
      )}
    </div>
  );
};

export default MatchupsList;
